/**
 * Dashboard of standalone (Preact + htm) visualizations for ONE form schema. From here a manager
 * creates a new visualization (→ editor page), edits an existing one, renames it, sets it as the
 * default, or deletes it. Each card shows a live thumbnail of the design rendered against the form's
 * newest submission. NEW parallel viz track — no "Kind" picker (unlike the legacy manager).
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart3, LayoutGrid, Table2, Plus, Star, Trash2, Pencil, Sparkles, X as XIcon, Eye, EyeOff } from 'lucide-react';

import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, ConfirmDialog, SkeletonCards } from '../components';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/Badge';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody } from '../components/ModalStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { FormGroup, FormLabel } from '../components/FormStyles';
import { StandaloneVizFrame } from '../components/standalone/StandaloneVizFrame';
import { formSchemaService, formSubmissionService } from '../api';
import standaloneVizService, { vizType } from '../api/services/standaloneVizService';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Crumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.muted};
  margin-bottom: ${p => p.theme.spacing.sm};
  a { color: inherit; cursor: pointer; text-decoration: none; }
  a:hover { color: ${p => p.theme.colors.tint}; }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

// Per-section header (Visualizations / Compact cards). Same shape as the page Header, but with a top
// rule between sections so the two object types read as distinct groups.
const SectionHeader = styled(Header)`
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.lg};
  border-top: 1px solid ${p => p.theme.colors.border};
  &:first-of-type { margin-top: 0; padding-top: 0; border-top: none; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${p => (p.$card ? '260px' : '320px')}, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

// The shared Card is flat — this is a local hover-able card so the dashboard feels alive.
const VizCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(35,37,98,0.06);
  }
`;

const ThumbWrap = styled.div`
  position: relative;
  height: 140px;
  overflow: hidden;
  background: ${p => p.theme.colors.background};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ThumbPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => `${p.theme.colors.tint}12`};
  color: ${p => p.theme.colors.tint};
`;

const CardBody = styled.div`
  padding: ${p => p.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.xs};
`;

const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${p => p.theme.spacing.sm};
`;

const Meta = styled.span`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  font-variant-numeric: tabular-nums;
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.xs};
  margin-top: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

// Card-only: the per-card "appears in submissions" admin toggle (independent of the default-card concept).
const ShowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.sm};
  margin-top: ${p => p.theme.spacing.sm};
  padding-top: ${p => p.theme.spacing.sm};
  border-top: 1px dashed ${p => p.theme.colors.border};
`;

const ShowLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => (p.$danger ? p.theme.colors.danger : p.theme.colors.muted)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { color: ${p => (p.$danger ? p.theme.colors.danger : p.theme.colors.primary)}; border-color: ${p => (p.$danger ? p.theme.colors.danger : p.theme.colors.primary)}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EmptyBox = styled.div`
  border: 1px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  text-align: center;
  color: ${p => p.theme.colors.muted};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.muted};
  padding: 4px;
  display: inline-flex;
  align-items: center;
  &:hover { color: ${p => p.theme.colors.text}; }
`;

const NameInput = styled.input`
  width: 100%;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.sm};
  font-size: ${p => p.theme.typography.sizes.default};
  color: ${p => p.theme.colors.text};
  background: ${p => p.theme.colors.background};
  &:focus { outline: none; border-color: ${p => p.theme.colors.tint}; }
`;

export const StandaloneVizManagerPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { formId } = useParams();
  const formSchemaId = parseInt(formId, 10);

  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbSubmissionId, setThumbSubmissionId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState('visualization'); // 'visualization' | 'card'
  const [createName, setCreateName] = useState('Submission view');
  const [creating, setCreating] = useState(false);

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [formData, list, subs] = await Promise.all([
        formSchemaService.getFormSchemaById(formSchemaId),
        standaloneVizService.listVisualizations(formSchemaId),
        formSubmissionService.getSubmissionsByFormId(formSchemaId, { page: 1, pageSize: 1 }).catch(() => null),
      ]);
      setForm(formData);
      setItems(Array.isArray(list) ? list : []);
      const subList = subs?.items || subs?.submissions || (Array.isArray(subs) ? subs : []) || [];
      setThumbSubmissionId(subList[0]?.id ?? null);
    } catch (err) {
      toast.error('Failed to load visualizations.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formSchemaId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSchemaId]);

  const sortByDefault = (arr) =>
    [...arr].sort((a, b) => {
      if (!!a.isDefault !== !!b.isDefault) return a.isDefault ? -1 : 1;
      return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
    });

  // Split the form's visualizations into the three object types (legacy rows → 'visualization').
  const vizItems = useMemo(() => sortByDefault(items.filter((v) => vizType(v) === 'visualization')), [items]);
  const cardItems = useMemo(() => sortByDefault(items.filter((v) => vizType(v) === 'card')), [items]);
  const listItems = useMemo(() => sortByDefault(items.filter((v) => vizType(v) === 'list')), [items]);

  // ── Create ─────────────────────────────────────────────────────────────────
  const createNameFor = (type) =>
    type === 'card' ? 'Compact card' : type === 'list' ? 'Submissions dashboard' : 'Submission view';
  const createTitleFor = (type) =>
    type === 'card' ? 'New compact card' : type === 'list' ? 'New list visualization' : 'New visualization';

  const openCreate = (type) => {
    setCreateType(type);
    setCreateName(createNameFor(type));
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error(createType === 'card' ? 'Please name the card.' : 'Please name the visualization.');
      return;
    }
    setCreating(true);
    try {
      const created = await standaloneVizService.createVisualization(formSchemaId, name, createType);
      setCreateOpen(false);
      navigate(`/forms/${formSchemaId}/visualizations/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create.');
    } finally {
      setCreating(false);
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────────
  const openRename = (v, e) => {
    e?.stopPropagation();
    setRenameTarget(v);
    setRenameName(v.name || '');
  };

  const handleRename = async () => {
    if (!renameTarget) return;
    const name = renameName.trim();
    if (!name) {
      toast.error('Please enter a name.');
      return;
    }
    setRenaming(true);
    try {
      await standaloneVizService.updateVisualization(formSchemaId, renameTarget.id, { name });
      toast.success('Visualization renamed.');
      setRenameTarget(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to rename.');
    } finally {
      setRenaming(false);
    }
  };

  // ── Set default ────────────────────────────────────────────────────────────
  const handleSetDefault = async (id, e) => {
    e?.stopPropagation();
    try {
      await standaloneVizService.setDefaultVisualization(formSchemaId, id);
      toast.success('Default visualization updated.');
      loadAll();
    } catch (err) {
      toast.error('Failed to set default.');
      console.error(err);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await standaloneVizService.deleteVisualization(formSchemaId, confirmDeleteId);
      toast.success('Visualization deleted.');
      setConfirmDeleteId(null);
      loadAll();
    } catch (err) {
      toast.error('Failed to delete.');
      console.error(err);
    }
  };

  const goEdit = (id) => navigate(`/forms/${formSchemaId}/visualizations/${id}`);

  // ── Show in submission (cards only) ──────────────────────────────────────────
  // Per-form admin control: which compact cards render inline on a submission's detail page. Optimistic;
  // reverts on failure. Independent of the single "default card" — any number of cards may be shown.
  const handleToggleShowInSubmission = async (v, e) => {
    e?.stopPropagation();
    const next = !(v.showInSubmission !== false);
    setItems((prev) => prev.map((it) => (it.id === v.id ? { ...it, showInSubmission: next } : it)));
    try {
      await standaloneVizService.setShowInSubmission(formSchemaId, v.id, next);
    } catch (err) {
      setItems((prev) => prev.map((it) => (it.id === v.id ? { ...it, showInSubmission: !next } : it)));
      toast.error('Failed to update card visibility.');
      console.error(err);
    }
  };

  // One card in either section — identical chrome; the card vs full distinction is purely organizational.
  const renderVizCard = (v) => {
    const kind = vizType(v);
    const isCard = kind === 'card';
    const isList = kind === 'list';
    // List vizzes render the form-wide saved config even with no design (the kit's auto-list view is the fallback),
    // so they always thumbnail; cards/full vizzes need a design + a sample submission.
    const canThumb = isList ? true : (v.hasDesign && thumbSubmissionId);
    const shown = v.showInSubmission !== false; // default true (legacy / undefined → shown)
    return (
      <VizCard key={v.id} theme={theme} onClick={() => goEdit(v.id)}>
        <ThumbWrap theme={theme}>
          {canThumb ? (
            isList ? (
              <StandaloneVizFrame
                mode="list"
                formSchemaId={formSchemaId}
                vizId={v.id}
                thumbnail
                title={`${v.name} preview`}
              />
            ) : (
              <StandaloneVizFrame
                submissionId={thumbSubmissionId}
                vizId={v.id}
                thumbnail
                title={`${v.name} preview`}
              />
            )
          ) : (
            <ThumbPlaceholder theme={theme}>
              {isList ? <Table2 size={36} /> : <BarChart3 size={36} />}
            </ThumbPlaceholder>
          )}
        </ThumbWrap>

        <CardBody theme={theme}>
          <CardTitleRow>
            <div style={{ minWidth: 0 }}>
              <ThemedText type="defaultSemiBold">{v.name}</ThemedText>
              <Meta theme={theme} style={{ display: 'block' }}>
                {v.hasDesign ? 'Ready' : 'Not generated'}
                {v.updatedAt ? ` · Updated ${new Date(v.updatedAt).toLocaleDateString()}` : ''}
              </Meta>
            </div>
            {v.isDefault && (
              <StatusBadge isActive title="Default for this type">
                <Star size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Default
              </StatusBadge>
            )}
          </CardTitleRow>

          <CardActions>
            {!v.isDefault && (
              <IconBtn theme={theme} onClick={(e) => handleSetDefault(v.id, e)} title="Set as default">
                <Star size={13} /> Set default
              </IconBtn>
            )}
            <IconBtn theme={theme} onClick={(e) => { e.stopPropagation(); goEdit(v.id); }} title="Edit">
              <Pencil size={13} /> Edit
            </IconBtn>
            <IconBtn theme={theme} onClick={(e) => openRename(v, e)} title="Rename">
              <Pencil size={13} /> Rename
            </IconBtn>
            <IconBtn theme={theme} $danger onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(v.id); }} title="Delete">
              <Trash2 size={13} />
            </IconBtn>
          </CardActions>

          {isCard && (
            <ShowRow theme={theme} onClick={(e) => e.stopPropagation()}>
              <ShowLabel theme={theme}>
                {shown ? <Eye size={13} /> : <EyeOff size={13} />}
                Show in submissions
              </ShowLabel>
              <ToggleSwitch
                theme={theme}
                title={shown ? 'Shown on submission pages' : 'Hidden from submission pages'}
                onClick={(e) => e.stopPropagation()}
              >
                <ToggleInput
                  type="checkbox"
                  checked={shown}
                  onChange={(e) => handleToggleShowInSubmission(v, e)}
                />
                <ToggleSlider theme={theme} $checked={shown} />
              </ToggleSwitch>
            </ShowRow>
          )}
        </CardBody>
      </VizCard>
    );
  };

  return (
    <Layout>
      <Helmet><title>{form ? `${form.name} · Visualizations` : 'Visualizations'}</title></Helmet>
      <PageContainer>
        <Crumb theme={theme}>
          <a onClick={() => navigate('/forms')}>Forms</a>
          <span>/</span>
          <a onClick={() => navigate(`/forms/${formSchemaId}/submissions`)}>
            {form?.name || `Form #${formSchemaId}`}
          </a>
          <span>/</span>
          <strong>Visualizations</strong>
        </Crumb>

        {/* ── Section: full-page Visualizations ──────────────────────────────── */}
        <SectionHeader theme={theme}>
          <div>
            <ThemedText type="subtitle">
              <BarChart3 size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Visualizations
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              Full-page AI layouts that render every submission of this form. Pick one as the default.
            </ThemedText>
          </div>
          <Button variant="primary" onClick={() => openCreate('visualization')}>
            <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            New visualization
          </Button>
        </SectionHeader>

        {loading ? (
          <SkeletonCards count={3} theme={theme} />
        ) : vizItems.length === 0 ? (
          <EmptyBox theme={theme}>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
              No visualizations yet
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
              Describe the dashboard you want — the AI authors a small mini-project tailored to this form's fields.
            </ThemedText>
            <Button variant="primary" onClick={() => openCreate('visualization')}>
              <Sparkles size={14} style={{ marginRight: 4 }} /> Create your first visualization
            </Button>
          </EmptyBox>
        ) : (
          <Grid>{vizItems.map(renderVizCard)}</Grid>
        )}

        {/* ── Section: compact Cards (the submission-page inline gadget) ──────── */}
        <SectionHeader theme={theme} id="cards">
          <div>
            <ThemedText type="subtitle">
              <LayoutGrid size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Compact cards
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              Small inline cards shown on each submission's detail page. Toggle "Show in submissions" on as many as you like.
            </ThemedText>
          </div>
          <Button variant="primary" onClick={() => openCreate('card')}>
            <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            New card
          </Button>
        </SectionHeader>

        {loading ? (
          <SkeletonCards count={2} theme={theme} />
        ) : cardItems.length === 0 ? (
          <EmptyBox theme={theme}>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
              No compact cards yet
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
              A card is a small, glanceable summary that renders inline on every submission's detail page.
            </ThemedText>
            <Button variant="primary" onClick={() => openCreate('card')}>
              <Sparkles size={14} style={{ marginRight: 4 }} /> Create your first card
            </Button>
          </EmptyBox>
        ) : (
          <Grid $card>{cardItems.map(renderVizCard)}</Grid>
        )}

        {/* ── Section: List visualizations (aggregate across many submissions) ── */}
        <SectionHeader theme={theme} id="lists">
          <div>
            <ThemedText type="subtitle">
              <Table2 size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              List visualizations
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              Dashboards that aggregate across many submissions of this form over a saved filter (top-N, trends, breakdowns).
            </ThemedText>
          </div>
          <Button variant="primary" onClick={() => openCreate('list')}>
            <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            New list visualization
          </Button>
        </SectionHeader>

        {loading ? (
          <SkeletonCards count={2} theme={theme} />
        ) : listItems.length === 0 ? (
          <EmptyBox theme={theme}>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
              No list visualizations yet
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
              A list visualization summarizes many submissions at once — choose the fields, a filter, and a limit, then design how the set is shown.
            </ThemedText>
            <Button variant="primary" onClick={() => openCreate('list')}>
              <Sparkles size={14} style={{ marginRight: 4 }} /> Create your first list visualization
            </Button>
          </EmptyBox>
        ) : (
          <Grid>{listItems.map(renderVizCard)}</Grid>
        )}

        {confirmDeleteId && (
          <ConfirmDialog
            isOpen={!!confirmDeleteId}
            title="Delete visualization?"
            message="This removes the visualization and its design. This can't be undone."
            confirmLabel="Delete"
            confirmVariant="danger"
            onConfirm={handleDelete}
            onCancel={() => setConfirmDeleteId(null)}
          />
        )}

        {createOpen && (
          <ModalOverlay $isOpen={createOpen} onClick={() => !creating && setCreateOpen(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '90vw' }}>
              <ModalHeader>
                <ThemedText type="defaultSemiBold">
                  {createTitleFor(createType)}
                </ThemedText>
                <CloseBtn theme={theme} onClick={() => setCreateOpen(false)} aria-label="Close" disabled={creating}>
                  <XIcon size={18} />
                </CloseBtn>
              </ModalHeader>
              <ModalBody>
                <FormGroup theme={theme}>
                  <FormLabel theme={theme}>Name (you can rename later)</FormLabel>
                  <NameInput
                    theme={theme}
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreateOpen(false); }}
                    placeholder={createType === 'card' ? 'Compact card' : 'Submission view'}
                    autoFocus
                  />
                </FormGroup>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                  <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleCreate} disabled={creating}>
                    {creating ? 'Creating…' : 'Create'}
                  </Button>
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}

        {renameTarget && (
          <ModalOverlay $isOpen={!!renameTarget} onClick={() => !renaming && setRenameTarget(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '90vw' }}>
              <ModalHeader>
                <ThemedText type="defaultSemiBold">Rename visualization</ThemedText>
                <CloseBtn theme={theme} onClick={() => setRenameTarget(null)} aria-label="Close" disabled={renaming}>
                  <XIcon size={18} />
                </CloseBtn>
              </ModalHeader>
              <ModalBody>
                <FormGroup theme={theme}>
                  <FormLabel theme={theme}>Name</FormLabel>
                  <NameInput
                    theme={theme}
                    value={renameName}
                    onChange={(e) => setRenameName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenameTarget(null); }}
                    placeholder="Visualization name"
                    autoFocus
                  />
                </FormGroup>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                  <Button variant="secondary" onClick={() => setRenameTarget(null)} disabled={renaming}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleRename} disabled={renaming}>
                    {renaming ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default StandaloneVizManagerPage;
